import { create } from "zustand";
import { useBrandStore } from "./brandStore";
import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import React from "react";

interface Project {
    id: number;
    project: string;
    type: string[];
    href: string;
    marketingContentPath: string[];
    status: string;
    timestamp: string;
}

interface ProjectState {
    projects: Project[];
    isInitialized: boolean;
    isLoading: boolean;
    setProjects: (projects: Project[]) => void;
    fetchProjects: (brandId: number) => Promise<void>;
}

export const useProjectStore = create<ProjectState>()((set, get) => {
    const fetchProjects = async (brandId: number) => {
        try {
            set({ isLoading: true });
            const useMockAuthentication = import.meta.env.VITE_USE_MOCK_AUTH === "true";
            let idToken;
            
            if (useMockAuthentication) {
                idToken = "123";
            } else {
                const { idToken: token } = (await fetchAuthSession()).tokens ?? {};
                idToken = token;
            }

            if (!idToken) {
                throw new Error("No authentication token found");
            }

            const response = await axios.get<Project[]>(
                `${import.meta.env.VITE_API_GATEWAY_URL}/get-projects`,
                {
                    headers: {
                        Authorization: `Bearer ${idToken.toString()}`,
                    },
                    params: {
                        brandId,
                    },
                }
            );
            console.log(`response.data: ${response.data}`);

            set({ projects: response.data, isInitialized: true });
        } catch (error) {
            console.error("Error fetching projects:", error);
            set({ projects: [], isInitialized: true });
        } finally {
            set({ isLoading: false });
        }
    };

    // Subscribe to brand store changes
    useBrandStore.subscribe(
        (state) => {
            if (get().isInitialized) {
                fetchProjects(state.selectedBrand);
            }
        }
    );

    return {
        projects: [],
        isInitialized: false,
        isLoading: false,
        setProjects: (projects) => set({ projects }),
        fetchProjects
    };
});

// Custom hook to handle initialization
export const useProjects = () => {
    const { projects, isInitialized, isLoading, fetchProjects } = useProjectStore();
    console.log(`isInitialized: ${isInitialized}`);
    React.useEffect(() => {
        if (!isInitialized) {
            fetchProjects(useBrandStore.getState().selectedBrand);
        }
    }, [isInitialized]);

    return { projects, isLoading };
};